import { h, Fragment } from "preact";
import { useState, useEffect, useRef, useMemo } from "preact/hooks";
import unifyGroupOptions from "../../helpers/unifyGroupOptions";
import unifyGroupVariants from "../../helpers/unifyGroupVariants";
import { IdPrefixContext } from "../../helpers/idPrefixContext";
import ProductOption from "../product-options/product-option";
import variantHasOptionValue from "../../helpers/variantHasOptionValue";
import ProductSetItem from "./product-set-item";
import setItemStockStatus from "./setItemStockStatus";
import { productOptionSwatchStyles } from "../product-options/product-option-styles";
import ProductSetAdd from "./product-set-add";
import StockStatus from "../stock-status/stock-status";
import setItemLayouts from "./setItemLayouts";

const optionValueExists = ({ options, optionName, optionValue }) =>
  options.some(
    (option) =>
      option.optionName === optionName &&
      option.optionValues.some(({ name }) => optionValue === name),
  );

const findVariantForOptions = ({ product, optionValues }) => {
  const applicableOptionValues = optionValues.filter((optionValue) =>
    product.variants.some((variant) =>
      variantHasOptionValue({ variant, optionValue }),
    ),
  );

  if (!applicableOptionValues.length) {
    return null;
  }

  const firstMatchingVariant = product.variants.find((variant) =>
    applicableOptionValues.every((optionValue) =>
      variantHasOptionValue({ variant, optionValue }),
    ),
  );

  return firstMatchingVariant || null;
};

const findFirstInStockVariant = ({ variants, quantity }) => {
  return variants.find((variant) => {
    const { inStock } = setItemStockStatus({
      variant,
      quantity,
    });

    return inStock;
  });
};

const STOCK_OPTIONS = {
  all: "all",
  inStock: "inStock",
};

const findInitialVariant = ({
  product,
  initialGlobalVariant,
  initialSetItemVariantId,
}) => {
  if (initialGlobalVariant) {
    return (
      findVariantForOptions({
        product,
        optionValues: initialGlobalVariant.selectedOptions.map(
          (option) => option.value,
        ),
      }) || product.variants[0]
    );
  }

  const initialVariant = product.variants.find(
    (variant) => variant.id === initialSetItemVariantId,
  );

  return initialVariant || product.variants[0];
};

export default function ProductSetContent({
  idPrefix,
  initialVariantId,
  productData,
  setProductsData,
}) {
  const showGlobalSelectors = !productData.helpers.hasOnlyDefaultVariant;
  const initialVariant = productData.variants.find(
    (variant) => variant.id === initialVariantId,
  );

  console.log(`productData`, productData);

  const [items, setItems] = useState(
    setProductsData.map((product, i) => {
      return {
        variant: findInitialVariant({
          product,
          initialGlobalVariant: showGlobalSelectors && initialVariant,
          initialSetItemVariantId: productData.setProducts?.[i].defaultVariant,
        }),
        quantity: JSON.parse(productData.setProducts?.[i].quantity || 1),
        requestedQuantity: JSON.parse(
          productData.setProducts?.[i].quantity || 1,
        ),
        setItemId: `${idPrefix}-set-item-${i}`,
      };
    }),
  );

  const itemsHash = items
    .map(({ variant, quantity }) => `${variant.id}-${quantity}`)
    .join();

  const [options, setOptions] = useState([]);
  const [variants, setVariants] = useState([]);
  const [mostRecentOption, setMostRecentOption] = useState(null);
  const [stockSelector, setStockSelector] = useState(STOCK_OPTIONS.all);
  const [selectedOptionValues, setSelectedOptionValues] = useState({});
  const ref = useRef(null);

  const itemStockStatuses = useMemo(() => {
    return items.map(({ variant, quantity }) => ({
      variant,
      quantity,
      status: setItemStockStatus({ variant, quantity }),
    }));
  }, [itemsHash]);

  const sortedItemStockStatuses = useMemo(() => {
    return itemStockStatuses
      .filter((item) => item.quantity > 0)
      .toSorted((itemA, itemB) => {
        if (itemA.status.inStock && itemB.status.inStock) return 0;

        if (itemA.status.inStock) return 1;
        if (itemB.status.inStock) return -1;

        if (!itemA.status.backOrderDate && !itemB.status.backOrderDate)
          return 0;

        if (!itemA.status.backOrderDate) return -1;
        if (!itemB.status.backOrderDate) return 1;

        return (
          new Date(itemB.status.backOrderDate).valueOf() -
          new Date(itemA.status.backOrderDate).valueOf()
        );
      });
  }, [itemStockStatuses]);

  const disabledOptionValues = useMemo(() => {
    const newDisabledOptionValues = {};

    options.forEach((option) => {
      newDisabledOptionValues[option.optionName] = [];
      const isMostRecentOption =
        option.optionName === mostRecentOption.optionName;

      option.optionValues.forEach((optionValue) => {
        const matchingVariants = [];
        setProductsData.forEach((setProduct, i) => {
          const itemQuantity = items[i].quantity;
          let hasAvailableVariant = false;
          let hasMatchingVariant = false;

          setProduct.variants.forEach((variant) => {
            const matchesMostRecent = isMostRecentOption
              ? true
              : variantHasOptionValue({
                  variant,
                  optionValue: mostRecentOption.optionValue,
                });

            const matchesOptions =
              variantHasOptionValue({
                variant,
                optionValue: optionValue.name,
              }) && matchesMostRecent;

            if (!matchesOptions) return;
            matchingVariants.push(variant);
            hasMatchingVariant = true;

            const { inStock } = setItemStockStatus({
              variant,
              quantity: itemQuantity,
            });
            if (inStock) {
              hasAvailableVariant = true;
            }
          });

          if (hasMatchingVariant && !hasAvailableVariant) {
            newDisabledOptionValues[option.optionName].push(optionValue.name);
          }
        });
      });
    });

    return newDisabledOptionValues;
  }, [itemsHash, mostRecentOption, options]);

  const ignoreDisabledOptions = stockSelector === STOCK_OPTIONS.all;

  useEffect(() => {
    const totalPrice = items.reduce(
      (total, item) =>
        total + parseFloat(item.variant.price.amount) * item.quantity,
      0,
    );

    const priceEls = document.querySelectorAll(
      `[data-set-price="${productData.id.replace("gid://shopify/Product/", "")}"]`,
    );

    priceEls.forEach((el) => {
      el.setAttribute("dynamic", "");
      el.setAttribute("price", totalPrice);
      el.setAttribute(
        "currency",
        setProductsData[0].variants[0].price.currencyCode,
      );
      el.style.display = totalPrice > 0 ? null : "none";
    });
  }, [itemsHash]);

  useEffect(() => {
    const newSelectedOptionValues = {};

    options.forEach((option) => {
      newSelectedOptionValues[option.optionName] = null;

      const selectedOptionValue = option.optionValues.find((optionValue) => {
        const eligibleItems = items.filter((item) =>
          item.variant.product.options.some((option) =>
            option.optionValues.find(({ name }) => name === optionValue.name),
          ),
        );

        return (
          eligibleItems.length > 0 &&
          eligibleItems.every(({ variant }) =>
            variantHasOptionValue({ variant, optionValue: optionValue.name }),
          )
        );
      });

      if (selectedOptionValue) {
        newSelectedOptionValues[option.optionName] = selectedOptionValue.name;
      }
    });

    setSelectedOptionValues(newSelectedOptionValues);
  }, [itemsHash, setSelectedOptionValues, options]);

  const updateItem = ({ index, data }) => {
    const newItems = [...items];
    const newQuantity =
      "quantity" in data ? data.quantity : newItems[index].quantity;
    const newItem = {
      ...(newItems[index] || {}),
      requestedQuantity: newQuantity,
      ...data,
      setItemId: `${idPrefix}-set-item-${index}`,
    };
    newItems[index] = newItem;

    setItems(newItems);
  };

  const updateItems = (data) => {
    const newItems = [...items].map((item, i) => {
      const newQuantity =
        "quantity" in data[i] ? data[i].quantity : item.quantity;
      const newItem = {
        ...item,
        requestedQuantity: newQuantity,
        ...data[i],
        setItemId: `${idPrefix}-set-item-${i}`,
      };

      return newItem;
    });

    setItems(newItems);
  };

  const updateItemAndCheckStock = ({ index, data }) => {
    if (stockSelector === STOCK_OPTIONS.inStock) {
      const variant = data.variant || items[index].variant;
      const quantity =
        "quantity" in data ? data.quantity : items[index].quantity;

      const { inStock } = setItemStockStatus({ variant, quantity });

      if (!inStock) {
        const newItems = items.map((item) => ({
          quantity: item.requestedQuantity,
        }));

        if (data.variant) {
          newItems[index].variant = data.variant;
        }

        if ("quantity" in data) {
          newItems[index].quantity = data.quantity;
        }

        setStockSelector(STOCK_OPTIONS.all);
        updateItems(newItems);
        return;
      }
    }

    updateItem({ index, data });
  };

  const selectedVariant =
    variants.find((variant) =>
      variant.selectedOptions.every((selectedOption) => {
        return (
          selectedOptionValues[selectedOption.name] === selectedOption.value
        );
      }),
    ) || null;

  const updateOptionValue = (newValue, optionName) => {
    if (!optionValueExists({ options, optionName, optionValue: newValue }))
      return;

    const newSelectedOptionValues = {
      ...selectedOptionValues,
    };

    newSelectedOptionValues[optionName] = newValue;
    const newSelectedOptionValuesArray = Object.values(newSelectedOptionValues);

    setSelectedOptionValues(newSelectedOptionValues);
    setMostRecentOption({
      optionName,
      optionValue: newValue,
    });

    const itemVariants = setProductsData.map((setProduct, i) => {
      const matchingVariant = findVariantForOptions({
        product: setProduct,
        optionValues: newSelectedOptionValuesArray,
      });

      return matchingVariant || items[i].variant;
    });

    const someItemsUnavailble = itemVariants.some((variant, i) => {
      const itemQuantity = items[i].quantity;
      const { inStock } = setItemStockStatus({
        variant,
        quantity: itemQuantity,
      });

      return !inStock;
    });

    if (someItemsUnavailble && stockSelector !== STOCK_OPTIONS.all) {
      setStockSelector(STOCK_OPTIONS.all);
    }

    updateItems(itemVariants.map((variant) => ({ variant })));
  };

  useEffect(() => {
    const unifiedVariants = unifyGroupVariants(productData, false);

    setVariants(unifiedVariants);

    const unifiedOptions = unifyGroupOptions(productData);
    setOptions(unifiedOptions);

    let initialVariant = null;

    if (!initialVariant) {
      initialVariant = unifiedVariants.find(
        (variant) => variant.id === initialVariantId,
      );
    }

    if (initialVariant) {
      setSelectedOptionValues(
        Object.fromEntries(
          initialVariant.selectedOptions.map((option) => [
            option.name,
            option.value,
          ]),
        ),
      );

      setMostRecentOption({
        optionName: initialVariant.selectedOptions[0].name,
        optionValue: initialVariant.selectedOptions[0].value,
      });
    } else {
      // Select first option value
      setSelectedOptionValues(
        Object.fromEntries(
          unifiedOptions.map((option) => [
            option.optionName,
            option.optionValues[0].name,
          ]),
        ),
      );

      setMostRecentOption({
        optionName: unifiedOptions[0].optionName,
        optionValue: unifiedOptions[0].optionValues[0].name,
      });
    }
  }, [
    productData,
    setSelectedOptionValues,
    setMostRecentOption,
    setVariants,
    setOptions,
    initialVariantId,
  ]);

  useEffect(() => {
    const handleSaveSetItem = (e) => {
      const { id, quantity } = e.detail.data;
      const { setItemId } = e.detail;
      const itemIndex = items.findIndex((item) => item.setItemId === setItemId);
      const setProduct = setProductsData[itemIndex];
      const newVariant = setProduct.variants.find(
        (variant) => variant.id === `gid://shopify/ProductVariant/${id}`,
      );

      updateItemAndCheckStock({
        index: itemIndex,
        data: { variant: newVariant, quantity },
      });
    };

    document.addEventListener("save-set-item", handleSaveSetItem);
    return () =>
      document.removeEventListener("save-set-item", handleSaveSetItem);
  }, [updateItem, setProductsData]);

  useEffect(() => {
    if (!ref) return;

    ref.current.dispatchEvent(
      new CustomEvent("variant:changed", {
        bubbles: true,
        detail: {
          variant: selectedVariant,
          optionsRole: null,
        },
      }),
    );
  }, [selectedVariant, ref]);

  const handleStockSelectorChange = (e) => {
    if (
      e.target.value === STOCK_OPTIONS.inStock &&
      stockSelector !== STOCK_OPTIONS.inStock
    ) {
      // Switched to Ready to ship
      if (showGlobalSelectors) {
        const newSelectedOptionValues = {
          ...selectedOptionValues,
        };

        options.forEach(({ optionName, optionValues }) => {
          const disabledValuesForOption =
            disabledOptionValues[optionName] || [];
          // Current option is not disabled
          if (
            !disabledValuesForOption.includes(selectedOptionValues[optionName])
          )
            return;

          const newValue = optionValues.find(
            (optionValue) =>
              !disabledValuesForOption.includes(optionValue.name),
          );

          if (newValue) {
            newSelectedOptionValues[optionName] = newValue.name;
            setMostRecentOption({
              optionName: optionName,
              optionValue: newValue.name,
            });
          }
        });

        const newSelectedOptionValuesArray = Object.values(
          newSelectedOptionValues,
        );

        setSelectedOptionValues(newSelectedOptionValues);

        const newItems = setProductsData.map((setProduct, i) => {
          let newVariant = findVariantForOptions({
            product: setProduct,
            optionValues: newSelectedOptionValuesArray,
          });

          const newItem = {};

          if (newVariant) {
            newItem.variant = newVariant;
          } else {
            if (itemStockStatuses[i].status.inStock) {
              newItem.variant = items[i].variant;
            } else {
              const firstInStockVariant = findFirstInStockVariant({
                variants: setProductsData[i].variants,
                quantity: items[i].quantity,
              });

              if (firstInStockVariant) {
                newItem.variant = firstInStockVariant;
              } else {
                newItem.variant = items[i].variant;
                newItem.quantity = 0;
                newItem.requestedQuantity = items[i].quantity;
              }
            }
          }

          console.log(`newItem`, newItem);
          return newItem;
        });

        updateItems(newItems);
      } else {
        const newItems = setProductsData.map((setProduct, i) => {
          const newItem = {};

          const { status } = itemStockStatuses[i];
          const currentVariant = items[i].variant;

          if (status.inStock) {
            newItem.variant = currentVariant;
          } else {
            const firstInStockVariant = findFirstInStockVariant({
              variants: setProduct.variants,
              quantity: items[i].quantity,
            });

            if (firstInStockVariant) {
              newItem.variant = firstInStockVariant;
            } else {
              newItem.variant = currentVariant;
              newItem.quantity = 0;
              newItem.requestedQuantity = items[i].quantity;
            }
          }

          return newItem;
        });

        updateItems(newItems);
      }
    } else if (
      e.target.value === STOCK_OPTIONS.all &&
      stockSelector !== STOCK_OPTIONS.all
    ) {
      // Switched to All
      const newItems = items.map((item) => ({
        quantity: item.requestedQuantity,
      }));

      updateItems(newItems);
    }

    setStockSelector(e.target.value);
  };

  return (
    <div className="product-form-set" ref={ref}>
      <fieldset className="product-form-set__stock-selector">
        <legend className="visually-hidden">
          {window.localeStrings.product.set_stock_accessible_label}
        </legend>
        <label className="product-form-set__stock-selector-label">
          <input
            className="product-form-set__stock-selector-input"
            type="radio"
            name={`${idPrefix}-stock-selector`}
            value={STOCK_OPTIONS.all}
            onChange={handleStockSelectorChange}
            checked={stockSelector === STOCK_OPTIONS.all}
          />
          <span className="product-form-set__stock-selector-label-text">
            {window.localeStrings.product.set_stock_all}
          </span>
        </label>
        <label className="product-form-set__stock-selector-label">
          <input
            className="product-form-set__stock-selector-input"
            type="radio"
            name={`${idPrefix}-stock-selector`}
            value={STOCK_OPTIONS.inStock}
            onChange={handleStockSelectorChange}
            checked={stockSelector === STOCK_OPTIONS.inStock}
          />
          <span className="product-form-set__stock-selector-label-text">
            {window.localeStrings.product.set_stock_in_stock}
          </span>
        </label>
      </fieldset>
      {showGlobalSelectors && (
        <div
          className={`product-form-set__global-options product-options ${variants.length ? "product-options--loaded" : ""}`}
          ref={ref}
        >
          <IdPrefixContext.Provider value={idPrefix}>
            {options.map((option, i) => (
              <ProductOption
                key={option.optionName}
                option={option}
                updateOptionValue={(newValue) =>
                  updateOptionValue(newValue, option.optionName)
                }
                selectedOptionValue={selectedOptionValues[option.optionName]}
                disabledOptionValues={
                  ignoreDisabledOptions
                    ? []
                    : disabledOptionValues[option.optionName]
                }
                last={i === options.length - 1}
                selectedVariant={selectedVariant}
                disabledSwatchStyle={productOptionSwatchStyles.dashed}
              />
            ))}
          </IdPrefixContext.Provider>
          {variants.length === 0 && (
            <div className="product-options__skeleton"></div>
          )}
        </div>
      )}
      <div className="product-form-set__items">
        {setProductsData &&
          setProductsData.map((setProduct, i) => (
            <ProductSetItem
              product={setProduct}
              item={items[i]}
              updateItem={(data) => updateItemAndCheckStock({ index: i, data })}
              inStock={
                stockSelector === STOCK_OPTIONS.all ||
                itemStockStatuses[i].status.inStock
              }
              itemId={items[i].setItemId}
              formStyle={
                showGlobalSelectors
                  ? setItemLayouts.drawer
                  : setItemLayouts.inline
              }
              idPrefix={`${idPrefix}-set-item-${i}`}
              enableAvailabilityPreview={
                stockSelector === STOCK_OPTIONS.inStock
              }
            />
          ))}
      </div>
      <div class="product-form__actions-stock-wrapper">
        <ProductSetAdd
          items={items}
          itemsHash={itemsHash}
          itemStockStatuses={itemStockStatuses}
        />
        {sortedItemStockStatuses.length > 0 && (
          <div className="product-form-set__global-stock">
            <StockStatus
              productId={sortedItemStockStatuses[0].variant.product.id}
              variantId={sortedItemStockStatuses[0].variant.id}
              quantityRequested={sortedItemStockStatuses[0].quantity}
              countryCode={window.theme.country}
            />
          </div>
        )}
      </div>
    </div>
  );
}
